/**
 * English UI catalogue of the note lane (W2-03 note-lane), namespace 'noteLane'. zh-CN lives in
 * src/i18n/zh-CN/noteLane.ts (localisation package W3-09). Command titles with a `<title>Hint` sibling read as a
 * sentence in the hint bar (`{keys}` marks the shortcut).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('noteLane', {
  // ---- panels and columns ----
  panelTimeline: 'Timeline',
  panelTools: 'Tools',
  laneColumn: 'Note lane',
  laneRegion: 'Note lane and motion columns',
  lineUnnamed: 'Line {index}',
  noLine: 'No line',

  // ---- tools ----
  toolsLabel: 'Note tools',
  toolSelect: 'Select tool',
  toolSelectHint: '{keys} picks the Select tool: click to select, drag to move or draw a box',
  toolTap: 'Tap tool',
  toolTapHint: '{keys} picks the Tap tool: click the lane to place Taps',
  toolDrag: 'Drag tool',
  toolDragHint: '{keys} picks the Drag tool',
  toolFlick: 'Flick tool',
  toolFlickHint: '{keys} picks the Flick tool',
  toolHold: 'Hold tool',
  toolHoldHint: '{keys} picks the Hold tool: press at the start and drag up to the end',
  toolEraser: 'Eraser tool',
  toolEraserHint: '{keys} picks the Eraser: click or drag across notes to delete them',
  toolSelectShort: 'Select',
  toolTapShort: 'Tap',
  toolDragShort: 'Drag',
  toolFlickShort: 'Flick',
  toolHoldShort: 'Hold',
  toolEraserShort: 'Eraser',
  generators: 'Generators',
  generatorsShort: 'Tools',
  genDistribute: 'Distribute (ramp values)',
  genClone: 'Clone to lines',
  genFill: 'Fill between notes',
  genPath: 'Motion path',
  genTemplates: 'Motion templates',
  genNeedsLine: 'Pick a line first',

  // ---- note kinds ----
  kindName: '{kind, select, tap {Tap} drag {Drag} flick {Flick} hold {Hold} other {Note}}',

  // ---- placement ----
  placeTap: 'Place a Tap at the mouse',
  placeTapHint: '{keys} places a Tap at the mouse',
  placeDrag: 'Place a Drag at the mouse',
  placeDragHint: '{keys} places a Drag at the mouse',
  placeFlick: 'Place a Flick at the mouse',
  placeFlickHint: '{keys} places a Flick at the mouse',
  placeTapAtPlayhead: 'Place a Tap at the playhead',
  placeTapAtPlayheadHint: '{keys} places a Tap at the playhead',
  placeDragAtPlayhead: 'Place a Drag at the playhead',
  placeDragAtPlayheadHint: '{keys} places a Drag at the playhead',
  placeFlickAtPlayhead: 'Place a Flick at the playhead',
  placeFlickAtPlayheadHint: '{keys} places a Flick at the playhead',
  holdAtMouse: 'Start or end a Hold at the mouse',
  holdAtMouseHint: '{keys} starts a Hold at the mouse; {keys} again ends it',
  holdAtPlayhead: 'Start or end a Hold at the playhead',
  holdAtPlayheadHint: '{keys} starts a Hold at the playhead; {keys} again ends it (works while playing)',
  cancelHold: 'Cancel the Hold being placed',
  mouseClick: 'Click',
  hintPlace: 'places a {kind} at {beat}, X {x}',
  hintPlaceHold: 'and drag up places a Hold from {beat}, X {x}',
  hintErase: 'or drag across notes deletes them',
  gestureEndHold: 'End the Hold',
  gestureCancel: 'Cancel',
  gestureNoSnap: 'Ignore snapping',
  gestureAxisLock: 'Move in one direction only',
  gestureAroundPlayhead: 'Around the playhead',
  gestureAroundZero: 'Around X 0',
  gestureStack: 'Place on top of another note',
  holdLength: '{beats} {one, select, yes {beat} other {beats}}, {sec} s',

  // ---- lane messages ----
  noLineTitle: 'No line selected',
  noLineBody: 'Pick a line in the lane header or the Lines panel to see and place its notes.',
  emptyLaneTitle: 'No notes on this line yet',
  emptyLaneBody:
    'Pick the Tap tool{tapTool, select, none {} other { ({tapTool})}} and click here{placeTap, select, none {.} other {, or point here and press {placeTap}.}}',
  uiMoverTitle: 'This line moves the {element} display',
  uiMoverBody: 'Notes on it would be invisible, so note tools are off.',
  uiElement:
    '{element, select, pause {pause button} combonumber {combo number} combo {combo label} score {score} bar {progress bar} name {song name} level {difficulty} other {interface}}',
  lockedTitle: 'This line is locked',
  lockedBody: 'Unlock it in the Lines panel to edit its notes.',

  // ---- reasons ----
  reasonNoProject: 'Open a project first',
  reasonNoLine: 'Pick a line first',
  reasonNoNotes: 'Select notes first',
  reasonNoHolds: 'Select Holds first',
  reasonNoSelection: 'Select notes or keyframes first',
  reasonNotOverLane: 'Point at the note lane first',
  reasonUiMover: 'This line moves a display element; notes on it would be invisible',
  reasonLocked: 'The line is locked (Lines panel)',
  reasonGesture: 'Finish the current drag first',
  reasonOpacityOnly: 'S sets opacity keyframes to 100 %; the selection also has other keyframes',
  reasonNoTimeRange: 'Drag in the time gutter to select a time range first',
  reasonNoMarkers: 'This chart has no markers yet',
  reasonNoMarker: 'Right-click a marker in the time gutter or select one first',
  reasonNoPaste: 'No paste ghost is up',
  reasonNoHoldPlacing: 'No Hold is being placed',
  reasonNoOtherLine: 'There is no other line',

  // ---- note editing ----
  mirror: 'Mirror X',
  mirrorHint: '{keys} mirrors X (around 0)',
  mirrorCenter: 'Mirror around the selection centre',
  mirrorCenterHint: '{keys} mirrors around the selection centre',
  flipSide: 'Flip side',
  flipSideHint: '{keys} flips the side (top / bottom)',
  setTop: 'Put on the top side',
  setBottom: 'Put on the bottom side',
  toggleFake: 'Toggle fake',
  toggleFakeHint: '{keys} makes notes fake or real',
  setFake: 'Make fake',
  setReal: 'Make real',
  toTap: 'Convert to Tap',
  toDrag: 'Convert to Drag',
  toFlick: 'Convert to Flick',
  toHold: 'Convert to Hold',
  snapX: 'Snap X to the grid',
  quantize: 'Snap times to the beat grid',
  reverse: 'Reverse in time',
  nudgeLater: 'Move later',
  nudgeLaterHint: '{keys} moves the selection one snap step later',
  nudgeEarlier: 'Move earlier',
  nudgeRight: 'Move right',
  nudgeRightHint: '{keys} moves notes half an X grid step',
  nudgeLeft: 'Move left',
  wheelWidth: 'Fine-tune note width',
  wheelWidthHint: '{keys} makes the selected notes wider or narrower',
  argWidthSteps: 'Steps of 10 % (negative: narrower)',
  holdLonger: 'Make Holds one step longer',
  holdShorter: 'Make Holds one step shorter',
  transformDialog: 'Transform selection...',
  argSteps: 'Steps',

  // ---- clipboard ----
  copy: 'Copy',
  copyHint: '{keys} copies the selection',
  cut: 'Cut',
  paste: 'Paste',
  pasteHint: '{keys} pastes at the mouse',
  pasteGhost: 'Paste with a ghost',
  pasteImmediate: 'Paste immediately',
  duplicate: 'Duplicate after the selection',
  duplicateHint: '{keys} duplicates right after the selection',
  delete: 'Delete',
  deleteHint: '{keys} deletes the selection',
  selectAll: 'Select all',
  selectAllHint: '{keys} selects everything in this column; again: the whole line',
  argPasteVariant: 'How to paste',
  pasteNormal: 'Paste',
  pasteMirrored: 'Mirrored paste',
  pasteAddValues: 'Add values onto the curves',
  pasteSpecialChoice: 'Paste special...',
  itemsNotes: '{count, plural, one {# note} other {# notes}}',
  itemsKeys: '{count, plural, one {# keyframe} other {# keyframes}}',
  itemsSegments: '{count, plural, one {# segment} other {# segments}}',
  itemsMarkers: '{count, plural, one {# marker} other {# markers}}',
  listJoin: '{a}, {b}',
  toastCopied: 'Copied {items} ({from} to {to})',
  toastCut: 'Cut {items} ({from} to {to})',
  toastPasted: 'Pasted {items}',
  toastUnmatched: '{count, plural, one {# value} other {# values}} found no keyframe to add to',
  actionPasteRest: 'Paste the rest as a new layer',
  // ---- usability sessions (W3-05, Appendix C H2; facilitator tooling) ----
  cmdUsabilityStart: 'Start a usability session',
  cmdUsabilityStop: 'Stop the usability session',
  uxProfileTitle: 'Who is taking part?',
  uxProfileFirstTime: 'A first-time charter',
  uxProfileRpe: 'An RPE user',
  uxChip: 'Task {n} of {count}: {title}',
  uxChipDone: 'All tasks finished',
  uxReached: 'Goal reached',
  uxDone: 'Done',
  uxGaveUp: 'Gave up',
  uxStop: 'Stop the session',
  uxStopped: 'Usability session stopped. Open #/dev/usability to save the log as a file.',
  uxPageTitle: 'Usability session',
  uxPageIntro:
    'Read each task aloud as written, then stay quiet and watch. The log records edits, keys, clicks, toasts and errors while a session runs; it stays in this browser until you save it.',
  uxPageStart: 'Start a session in the editor: open the command palette and run "Start a usability session".',
  uxSay: 'Say',
  uxObserve: 'Watch for',
  uxSuccess: 'Counts as reached when',
  uxLastLog: 'Last session: {participant} ({profile}), {tasks} tasks, {events} events',
  uxNoLog: 'No session has been logged in this browser yet.',
  uxSaveLog: 'Save the log (JSON)',
  uxClearLog: 'Delete the log',
  uxTaskOutcome: '{title}: {outcome}, {seconds} s, {commits} edits, {undos} undos',
  uxOutcomeNone: 'not finished',
  uxTaskPattern: 'Place a pattern',
  uxTaskPatternScript:
    'In the second bar, place a little rhythm that follows the music: eight notes, at least one of them a Hold.',
  uxTaskPatternObserve: 'Which tool or key they use, whether they find the beat snap, how long until the first note.',
  uxTaskPatternSuccess: 'eight new notes, one of them a Hold',
  uxTaskAnimate: 'Animate a line',
  uxTaskAnimateScript: 'Make the main line move from the left to the right over two beats, slowing down at the end.',
  uxTaskAnimateObserve: 'Whether they find keyframes (K, dragging the line, templates) and the curve picker.',
  uxTaskAnimateSuccess: 'a position segment of 100 px or more with a curve other than Linear',
  uxTaskFix: 'Fix a problem',
  uxTaskFixScript: 'The editor reports a problem in this chart. Find it and fix it.',
  uxTaskFixObserve: 'Whether they notice the problems chip, and use the fix button or fix it by hand.',
  uxTaskFixSuccess: 'fewer problems than when the task started',
  uxTaskTestPlay: 'Judged test play',
  uxTaskTestPlayScript: 'Play your chart as a player would, hitting the notes yourself, until the end.',
  uxTaskTestPlayObserve: 'Whether they find test play and the judged mode, and how they get back to editing.',
  uxTaskTestPlaySuccess: 'a test play reached its results',
  uxTaskExport: 'Export',
  uxTaskExportScript: 'Save the chart as a file you could open in Phira.',
  uxTaskExportObserve: 'Whether they find Publish and Export, and what they make of the checks before export.',
  uxTaskExportSuccess: 'a new export',
  settingWheelSeeks: 'Wheel moves the playhead (RPE)',
  settingWheelSeeksHelp:
    'Scrolling the timeline with the mouse wheel also moves the playhead, so the preview shows the moment on the playhead line. Alt+wheel scrolls faster.',
  wheelSeeksPreset: 'With the RPE keyboard preset',
  wheelSeeksOn: 'Always',
  wheelSeeksOff: 'Never (the wheel only scrolls)',
  toastJumpMade:
    '{count, plural, one {Made a jump at {at}} other {Made # jumps, the first at {at}}}: the value after a Stay cannot change where the Stay arrives',
  actionContinueFromPrevious: 'Continue from previous',
  toastDuplicated: 'Duplicated {items}',
  toastDeleted: 'Deleted {items}',
  toastNothingToPaste: 'The clipboard is empty: copy something first{keys, select, none {} other { ({keys})}}',
  toastNothingCopied: 'Select notes or keyframes to copy',
  toastMirrored: 'Mirrored {notes} and negated {keys}',
  toastErased: 'Erased {items}',
  ghostLabel:
    'Paste {count, plural, one {# note} other {# notes}} on {line}{mirror, select, yes { (mirrored)} other {}}: Enter or click places, Esc cancels',
  ghostLabelKeys: 'Paste {items} on {line}: Enter or click places, Esc cancels',
  ghostLabelLocked: '{line} is locked: Tab picks another line',
  ghostMirror: 'Mirror',
  ghostLine: 'Other line',
  ghostShift: 'One step later / earlier',
  ghostCommit: 'Paste here',
  ghostCancel: 'Cancel',
  pasteMirrorToggle: 'Paste ghost: mirror',
  pasteNextLine: 'Paste ghost: next line',
  pastePrevLine: 'Paste ghost: previous line',
  pasteLater: 'Paste ghost: one step later',
  pasteEarlier: 'Paste ghost: one step earlier',
  pasteCommit: 'Paste at the ghost',
  pasteCommitHint: '{keys} pastes at the ghost',
  pasteCancel: 'Cancel the paste',

  // ---- transaction labels ----
  txPlace: 'Place a {kind, select, tap {Tap} drag {Drag} flick {Flick} hold {Hold} other {note}}',
  txMove: 'Move {count, plural, one {# note} other {# notes}}',
  txMoveItems: 'Move {count, plural, one {# item} other {# items}}',
  txHoldLength: 'Change {count, plural, one {a Hold} other {# Holds}}',
  txDelete: 'Delete {items}',
  txMirror: 'Mirror {count, plural, one {# note} other {# notes}}',
  txFlip: 'Flip the side of {count, plural, one {# note} other {# notes}}',
  txSide: 'Set the side of {count, plural, one {# note} other {# notes}}',
  txFake: 'Change fake on {count, plural, one {# note} other {# notes}}',
  txConvert:
    'Convert {count, plural, one {# note} other {# notes}} to {kind, select, tap {Tap} drag {Drag} flick {Flick} hold {Hold} other {notes}}',
  txSnapX: 'Snap X of {count, plural, one {# note} other {# notes}}',
  txQuantize: 'Snap times of {count, plural, one {# note} other {# notes}}',
  txReverse: 'Reverse {count, plural, one {# item} other {# items}} in time',
  txNudge: 'Nudge {count, plural, one {# item} other {# items}}',
  txStretch: 'Stretch time of {count, plural, one {# item} other {# items}}',
  txScaleX: 'Scale X of {count, plural, one {# note} other {# notes}}',
  txPaste: 'Paste {items}',
  txCut: 'Cut {items}',
  txDuplicate: 'Duplicate {items}',
  txErase: 'Erase {count, plural, one {# note} other {# notes}}',
  txWidth: 'Change the width of {count, plural, one {# note} other {# notes}}',
  txInsertTime: 'Insert {beats} {one, select, yes {beat} other {beats}}',
  txRemoveTime: 'Remove {beats} {one, select, yes {beat} other {beats}}',
  txMarkerAdd: 'Add marker "{name}"',
  txMarkerRename: 'Rename marker to "{name}"',
  txMarkerDelete: 'Delete marker "{name}"',

  // ---- markers ----
  markerAdd: 'Add marker',
  markerAddHint: '{keys} adds a marker at the playhead',
  markerPrev: 'Previous marker',
  markerNext: 'Next marker',
  markerRename: 'Rename marker...',
  markerDelete: 'Delete marker',
  markerDefaultName: 'Marker {n}',
  toastMarkerAdded: 'Added "{name}" at {beat}',
  actionRename: 'Rename',
  toastNoPrevMarker: 'No marker before the playhead',
  toastNoNextMarker: 'No marker after the playhead',
  renameTitle: 'Rename marker',
  renameLabel: 'Name',
  renameConfirm: 'Rename',
  renameEmpty: 'Type a name for the marker',

  // ---- insert / remove time ----
  timeInsert: 'Insert beats at playhead...',
  timeRemove: 'Remove selected time range...',
  insertTitle: 'Insert beats at the playhead',
  insertBody:
    'Everything at or after {beat} moves later: notes, keyframes, tempo points, markers, effects and videos. A segment spanning that point is stretched (its curve keeps its shape).',
  insertBodyLines:
    'Notes and keyframes of the chosen lines at or after {beat} move later. A segment spanning that point is stretched.',
  insertLength: 'Beats to insert',
  insertConfirm: 'Insert',
  scopeLabel: 'Apply to',
  scopeAll: 'The whole chart',
  scopeActive: 'Only {line}',
  scopeChosen: 'These lines',
  removeTitle: 'Remove the selected time range',
  removeBody:
    'Notes from {from} up to {to} are deleted. Segments are split at both edges without changing their shape and joined across the gap, and everything later moves {beats} {one, select, yes {beat} other {beats}} earlier.',
  removeConfirm: 'Remove',
  cancel: 'Cancel',
  toastInserted:
    'Inserted {beats} {one, select, yes {beat} other {beats}}: moved {notes, plural, one {# note} other {# notes}}, {keys, plural, one {# keyframe} other {# keyframes}}, {tempo, plural, one {# tempo point} other {# tempo points}}',
  toastRemoved:
    'Removed {beats} {one, select, yes {beat} other {beats}}: deleted {deleted, plural, one {# note} other {# notes}}, moved {notes, plural, one {# note} other {# notes}}, {keys, plural, one {# keyframe} other {# keyframes}}, {tempo, plural, one {# tempo point} other {# tempo points}}',
  argBeats: 'Beats to insert',
  invalidLength: 'Type a length greater than 0',

  // ---- lane header and lane commands ----
  headerLine: 'Line',
  headerLinePick: 'Line whose notes you edit',
  addLine: 'Add a line',
  headerAlso: 'Also',
  headerAlsoAdd: 'Show the notes of another line as ghosts',
  headerAlsoRemove: 'Stop showing {line}',
  headerSides: 'Sides',
  sidesBoth: 'Both sides',
  sidesAbove: 'Top only',
  sidesBelow: 'Bottom only',
  headerXGrid: 'X grid lines',
  headerXSnap: 'Snap X to the grid',
  zoomXIn: 'Zoom the lane X in',
  zoomXOut: 'Zoom the lane X out',
  fitX: 'Fit the lane to the play field and the notes',
  plusX: 'On screen, +X points this way at the playhead (the lane shows the line unrotated)',
  plusXNone: 'No line to orient',
  pinLane: 'Pin this line as an extra lane',
  unpinLane: 'Remove this extra lane',
  makeActive: 'Edit this line',
  alsoShow: 'Also show the notes of a line',
  alsoHide: 'Stop showing the notes of a line',
  alsoClear: 'Stop showing other lines',
  sides: 'Show notes of one side',
  argLine: 'Line',
  argSides: 'Sides',
  holdMoveAlias: 'Hold to move notes with the mouse (RPE)',
  holdDeleteAlias: 'Hold and right-click notes to delete them (RPE)',
  laneHelpTitle: 'Note lane',
  laneHelpBody:
    'The note lane shows the active line unrotated: time flows up and the horizontal axis is the position along the line (RPE X, -675 to 675 on screen). Pick a tool on the left, or point and press the place-note keys (Q, W and E in the default keymap) to place Taps, Drags and Flicks; the Hold key (R) starts and ends a Hold. Drag notes to move them, drag empty space to select, right-click for more.',

  // ---- hover tooltip ----
  tipTitle: '{kind}{fake, select, yes { (fake)} other {}}',
  tipBeat: 'Beat {beat}',
  tipTime: 'Time {sec}',
  tipX: 'X {x}',
  tipLine: 'Line {line}',
  tipSide: '{above, select, yes {Top side} other {Bottom side}}',
  tipHoldEnd: 'Ends at {beat} ({length} {one, select, yes {beat} other {beats}})',
  tipStack: '{count} notes here: {alt}+click cycles through them',
  flagSimultaneous: 'Simultaneous: highlighted in Phira',
  flagCover: 'Hidden under the line now',
  flagNegativeAlpha: 'Hidden now: line opacity below 0',
  flagCulled: 'Culled now: negative speed or beyond the screen',
  flagUiMover: 'Invisible: on a UI mover line',
  flagBeforeAppear: 'Invisible now: before its appear time',
  flagOffscreen: 'Off screen now',
  flagFiltered: 'Hidden in the preview by a filter',
});

export default catalogOf(m);
